/**
 * Hub Service by karora on 11/03/17.
 */
import { map, catchError } from 'rxjs/operators';

import { HttpResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, throwError } from 'rxjs';

import { Person } from '../types/person';
import { Programme } from '../types/programme';
import { VoxResponse } from '../types/response';
import { Topic } from '../types/topic';
import { UserProfile } from '../types/user-profile';
import { ApiService } from './api.service';
import { ProfileService } from './profile.service';

@Injectable()
export class VoxPopService {
  private static baseURL = '/api';
  private static privateURL = VoxPopService.baseURL + '/private';
  private static adminURL = VoxPopService.baseURL + '/admin';

  constructor(
    private api: ApiService,
  ) { }

  private static getError(error: HttpResponse<any> | any) {
    let errMsg: string;
    const body = error.body.json() || '';
    const err = body.error || JSON.stringify(body);
    errMsg = `${error.status} - ${error.statusText || ''} ${err}`;
    console.error(errMsg);
    return throwError(errMsg);
  }

  private static updateError(error: any): Observable<any> {
    if (error.status !== 401) {
      console.log('VoxPop update error:', error);
    }
    let errMsg: string;
    if (error instanceof HttpResponse) {
      const body = error.body.json() || '';
      const err = body.error || JSON.stringify(body);
      errMsg = `${error.status} - ${error.statusText || ''} ${err}`;
    } else {
      errMsg = error.message ? error.message : error.toString();
    }
    console.error(errMsg);
    return throwError(errMsg);
  }

  getTopic(topicId: number): Observable<Topic> {
    console.log('Getting Topic ' + topicId);
    return this.api.Get(`${VoxPopService.privateURL}/t/details/${topicId}`).pipe(
      map(response => response.data as Topic),
      catchError(VoxPopService.getError)
    );
  }

  updateTopic(topic: Topic): Observable<Topic> {
    const url = `${VoxPopService.adminURL}/t/update/${topic.id}`;
    topic.id = Number(topic.id);
    topic.programme_id = Number(topic.programme_id);
    return this.api.Post(url, JSON.stringify(topic)).pipe(
      map(() => topic),
      catchError(VoxPopService.updateError));
  }

  createTopic(topic: Topic): Observable<Topic> {
    const url = `${VoxPopService.adminURL}/t/create`;
    topic.programme_id = Number(topic.programme_id);
    return this.api.Post(url, JSON.stringify(topic)).pipe(
      map(response => response.data as Topic),
      catchError(VoxPopService.updateError));
  }


  activeTopics(): Observable<Topic[]> {
    console.log('Getting Active Topics ');
    return this.api.Get(`${VoxPopService.privateURL}/t/all`, true).pipe(
      map(response => response.list as Topic[]),
      catchError(() => throwError('Unable to load details. Please retry.')));
  }

  activeWires(): Observable<Topic[]> {
    console.log('Getting Active Wires ');
    return this.api.Get(`${VoxPopService.privateURL}/w/all`, true).pipe(
      map(response => response.list as Topic[]),
      catchError(() => throwError('Unable to load details. Please retry.'))
    );
  }

  activeProgrammes(): Observable<Programme[]> {
    console.log('Getting Active Programmes');
    return this.api.Get(`${VoxPopService.privateURL}/prog/all`, true).pipe(
      map(response => response.list as Programme[]),
      catchError(() => throwError('Unable to load details. Please retry.'))
    );
  }

  myActiveProgrammes(propagateErrors: boolean = false): Observable<Programme[]> {
    console.log('Getting My Active Programmes');
    return this.api.Get(`${VoxPopService.privateURL}/prog/my/active`, propagateErrors).pipe(
      map(response => response.list as Programme[]),
      catchError(error => propagateErrors ? throwError('Unable to load programmes. Please retry.') : VoxPopService.getError(error))
    );
  }

  myProgrammes(): Observable<Programme[]> {
    console.log('Getting My Programmes');
    return this.api.Get(`${VoxPopService.privateURL}/prog/my/all`).pipe(
      map(response => response.list as Programme[]),
      catchError(VoxPopService.getError)
    );
  }

  getProgramme(programmeId: number): Observable<Programme> {
    console.log('Getting Programme ' + programmeId);
    return this.api.Get(`${VoxPopService.privateURL}/prog/details/${programmeId}`, true).pipe(
      map(response => response.data as Programme),
      catchError(() => throwError('Unable to load details. Please retry.'))
    );
  }

  updateProgramme(programme: Programme): Observable<Programme> {
    const url = `${VoxPopService.adminURL}/prog/update/${programme.id}`;
    programme.id = Number(programme.id);
    return this.api.Post(url, JSON.stringify(programme), true).pipe(
      map(response => {
        if (response.status !== 'success') { throw new Error('Save failed.'); }
        return programme;
      }),
      catchError(() => throwError('Unable to save changes. Please try again.'))
    );
  }

  createProgramme(programme: Programme): Observable<Programme> {
    const url = `${VoxPopService.adminURL}/prog/create/${programme.id}`;
    return this.api.Post(url, JSON.stringify(programme), true).pipe(
      map(response => {
        if (response.status !== 'success') { throw new Error('Save failed.'); }
        return response.data as Programme;
      }),
      catchError(() => throwError('Unable to save changes. Please try again.'))
    );
  }

  getCorrection(id: number): Observable<any> {
    return this.api.Get(`${VoxPopService.privateURL}/r/correction/${id}`, true);
  }
  requestCorrection(id: number): Observable<any> {
    return this.api.Post(`${VoxPopService.privateURL}/r/correction/${id}`, '{}', true);
  }
  decideCorrection(id: number, job: number, revision: number, decision: string): Observable<any> {
    return this.api.Post(`${VoxPopService.privateURL}/r/correction/${id}/decision`,
      JSON.stringify({job_id: job, editor_revision: revision, decision}), true);
  }

  updateResponse(response: VoxResponse, propagateErrors: boolean = false): Observable<VoxResponse> {
    const url = `${VoxPopService.privateURL}/r/update/${response.id}`;
    response.id = Number(response.id);
    return this.api.Post(url, JSON.stringify(response), propagateErrors).pipe(
      map(() => response),
      catchError(VoxPopService.updateError)
    );
  }

  getResponses(programmeId: number, topicId: number, personId: number, beforeResponseId: number): Observable<VoxResponse[]> {
    let url = `${VoxPopService.privateURL}/r/my`;
    if (!!topicId) {
      url = `${VoxPopService.privateURL}/r/t/${topicId}`;
    } else if (!!programmeId) {
      url = `${VoxPopService.privateURL}/r/prog/${programmeId}`;
    } else if (!!personId) {
      url = `${VoxPopService.privateURL}/r/p/${personId}`;
    } else if (ProfileService.CanReviewAll()) {
      url = `${VoxPopService.privateURL}/r/all`
    }
    if (!!beforeResponseId) {
      url += `?before=${beforeResponseId}`;
    }
    console.log('Getting responses for: ' + url);
    return this.api.Get(url, true).pipe(
      map(response => response.list as VoxResponse[]),
      catchError(() => throwError('Unable to load responses. Please retry.'))
    );
  }

  getPerson(personId: number): Observable<Person> {
    console.log('Getting Person ' + personId);
    return this.api.Get(`${VoxPopService.privateURL}/p/details/${personId}`, true).pipe(
      map(response => response.data as Person),
      catchError(() => throwError('Unable to load person details. Please reload to try again.'))
    );
  }

  allProfiles(): Observable<UserProfile[]> {
    console.log('Getting Profiles');
    return this.api.Get(`${VoxPopService.adminURL}/profiles/all`, true).pipe(
      map(response => response.list as UserProfile[]),
      catchError(() => throwError('Unable to load details. Please retry.'))
    );
  }

  getProfile(pType: number, profileId: string): Observable<UserProfile> {
    console.log('Getting User Profile ' + profileId);
    return this.api.Get(`${VoxPopService.adminURL}/profile/details/${pType}/${profileId}`, true).pipe(
      map(response => response.data as UserProfile),
      catchError(() => throwError('Unable to load details. Please retry.'))
    );
  }

  updateProfile(profile: UserProfile): Observable<UserProfile> {
    const url = `${VoxPopService.adminURL}/profile/update/${profile.type}/${profile.id}`;
    return this.api.Post(url, JSON.stringify(profile), true).pipe(
      map(response => {
        if (response.status !== 'success') { throw new Error('Save failed.'); }
        return profile;
      }),
      catchError(() => throwError('Unable to save changes. Please try again.'))
    );
  }

  updatePerson(person: Person): Observable<Person> {
    const url = `${VoxPopService.adminURL}/person/update/${person.id}`;
    person.person_type_id = Number(person.person_type_id);
    return this.api.Post(url, JSON.stringify(person), true).pipe(
      map(response => {
        if (response.status !== 'success') { throw new Error('Unable to save person.'); }
        return person;
      }),
      catchError(() => throwError('Unable to save person. Please try again.'))
    );
  }
}

